import PropTypes from 'prop-types';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';

// project imports
import { notifySessionExpired, registerSessionExpiredHandler } from 'utils/sessionExpired';

// ==============================|| AUTH CONTEXT ||============================== //
// Session-cookie based auth against apps/do-ra.cz/api (docs/ARCHITECTURE.md) -
// NOT JWT/localStorage. The backend sets an HttpOnly session cookie
// (API_DORA_SESSID); every request here uses credentials:'include' so the
// browser sends it, and every mutating request fetches a fresh CSRF token
// first (Core/Security/CsrfMiddleware requires the X-CSRF-Token header).

export const AuthContext = createContext(undefined);

async function fetchCsrfToken() {
  const res = await fetch('/auth/csrf-token', { credentials: 'include' });
  const body = await res.json();
  return body?.data?.csrf_token;
}

async function apiPost(path, payload) {
  const csrfToken = await fetchCsrfToken();
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
    body: JSON.stringify(payload)
  });
  const body = await res.json().catch(() => ({}));

  if (!res.ok || body.success === false) {
    if (res.status === 401) notifySessionExpired();
    const error = new Error(body.message || 'Požadavek se nezdařil.');
    error.code = body.code;
    error.status = res.status;
    throw error;
  }

  return body;
}

export function AuthProvider({ children }) {
  // status: 'loading' (initial /auth/me check in flight) | 'authenticated' | 'unauthenticated'
  const [state, setState] = useState({ status: 'loading', user: null });

  const refresh = useCallback(async () => {
    try {
      const res = await fetch('/auth/me', { credentials: 'include' });
      if (!res.ok) {
        setState({ status: 'unauthenticated', user: null });
        return;
      }
      const body = await res.json();
      setState({ status: 'authenticated', user: body.data });
    } catch {
      setState({ status: 'unauthenticated', user: null });
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Bugfix 2026-09-04: mimo počáteční /auth/me kontrolu se status sám od
  // sebe neaktualizuje - bez tohohle by na 401 uprostřed session zareagovala
  // jen ta jedna stránka (zobrazením chybové hlášky), ne AuthGuard. Jakmile
  // se status přepne na 'unauthenticated', AuthGuard při dalším renderu sám
  // přesměruje na /login se zapamatovanou cestou.
  useEffect(() => {
    registerSessionExpiredHandler(() => setState({ status: 'unauthenticated', user: null }));
    return () => registerSessionExpiredHandler(null);
  }, []);

  const login = useCallback(
    async (identifier, password) => {
      const body = await apiPost('/auth/login', { identifier, password });
      // /auth/login's response only carries id/identifier/first_name/last_name
      // (AuthController::userPayload()) - refresh() pulls the fuller /auth/me
      // shape (tenant_id, user_type, previous_login_at) right after.
      await refresh();
      return body.data;
    },
    [refresh]
  );

  const logout = useCallback(async () => {
    await apiPost('/auth/logout', {});
    setState({ status: 'unauthenticated', user: null });
  }, []);

  const register = useCallback(async (payload) => apiPost('/auth/register', payload), []);

  // Public, unauthenticated - lets the login/register pages hide the
  // registration link/form when the admin toggle (AdminRegistrationController)
  // is off, instead of only enforcing it silently server-side (a visitor
  // could otherwise see and fill in a form that always silently no-ops).
  const getRegistrationStatus = useCallback(async () => {
    const res = await fetch('/auth/registration-status', { credentials: 'include' });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || body.success === false) {
      if (res.status === 401) notifySessionExpired();
      throw new Error(body.message || 'Požadavek se nezdařil.');
    }
    return body.data;
  }, []);

  const confirmRegistration = useCallback(async (token) => apiPost('/auth/register/confirm', { token }), []);

  const forgotPassword = useCallback(async (payload) => apiPost('/auth/forgot-password', payload), []);

  const resetPassword = useCallback(async (token, password) => apiPost('/auth/reset-password', { token, password }), []);

  const setPin = useCallback(async (pin, pinConfirmation) => apiPost('/account/pin', { pin, pin_confirmation: pinConfirmation }), []);

  const requestPinChange = useCallback(async () => apiPost('/account/pin/change-request', {}), []);

  const confirmPinChange = useCallback(
    async (token, pin, pinConfirmation) => apiPost('/account/pin/change-confirm', { token, pin, pin_confirmation: pinConfirmation }),
    []
  );

  const completeInvite = useCallback(async (payload) => apiPost('/auth/invite/complete', payload), []);

  const getPinStatus = useCallback(async () => {
    const res = await fetch('/account/pin', { credentials: 'include' });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || body.success === false) {
      if (res.status === 401) notifySessionExpired();
      throw new Error(body.message || 'Požadavek se nezdařil.');
    }
    return body.data;
  }, []);

  const getNotificationSettings = useCallback(async () => {
    const res = await fetch('/account/notifications', { credentials: 'include' });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || body.success === false) {
      if (res.status === 401) notifySessionExpired();
      throw new Error(body.message || 'Požadavek se nezdařil.');
    }
    return body.data;
  }, []);

  const setLoginEmailNotify = useCallback(async (enabled) => apiPost('/account/notifications', { login_email_notify: enabled }), []);

  // "Alarm docházky" (2026-08-31, na výslovné přání uživatele) - stejný vzor jako notifications výše.
  const getAttendanceReminderSettings = useCallback(async () => {
    const res = await fetch('/account/attendance-reminder', { credentials: 'include' });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || body.success === false) {
      if (res.status === 401) notifySessionExpired();
      throw new Error(body.message || 'Požadavek se nezdařil.');
    }
    return body.data;
  }, []);

  const setAttendanceReminderEnabled = useCallback(
    async (enabled) => apiPost('/account/attendance-reminder', { attendance_reminder_enabled: enabled }),
    []
  );

  // Soukromý e-mail (2026-08-30) - cíl pro zaslání podepsané docházkové
  // karty, ODDĚLENÝ od přihlašovacího identifieru. Musí se ověřit klikem na
  // odkaz (confirmPrivateEmail) předtím, než ho appka kdekoliv použije.
  const getPrivateEmailStatus = useCallback(async () => {
    const res = await fetch('/account/private-email', { credentials: 'include' });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || body.success === false) {
      if (res.status === 401) notifySessionExpired();
      throw new Error(body.message || 'Požadavek se nezdařil.');
    }
    return body.data;
  }, []);

  const requestPrivateEmail = useCallback(async (email) => apiPost('/account/private-email', { email }), []);

  const confirmPrivateEmail = useCallback(async (token) => apiPost('/account/private-email/confirm', { token }), []);

  // PIN-gated reveal (2026-08-30, explicit security request) - status()
  // above never includes the address itself, this is the only path that
  // gets it back, and only for the owning user about themselves.
  const revealPrivateEmail = useCallback(async (pin) => {
    const body = await apiPost('/account/private-email/reveal', { pin });
    return body.data.email;
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      login,
      logout,
      register,
      getRegistrationStatus,
      confirmRegistration,
      forgotPassword,
      resetPassword,
      setPin,
      getPinStatus,
      requestPinChange,
      confirmPinChange,
      completeInvite,
      getNotificationSettings,
      setLoginEmailNotify,
      getAttendanceReminderSettings,
      setAttendanceReminderEnabled,
      getPrivateEmailStatus,
      requestPrivateEmail,
      confirmPrivateEmail,
      revealPrivateEmail,
      refresh
    }),
    [
      state,
      login,
      logout,
      register,
      getRegistrationStatus,
      confirmRegistration,
      forgotPassword,
      resetPassword,
      setPin,
      getPinStatus,
      requestPinChange,
      confirmPinChange,
      completeInvite,
      getNotificationSettings,
      setLoginEmailNotify,
      getAttendanceReminderSettings,
      setAttendanceReminderEnabled,
      getPrivateEmailStatus,
      requestPrivateEmail,
      confirmPrivateEmail,
      revealPrivateEmail,
      refresh
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = { children: PropTypes.node };
